<template>
  <div class="messenger-container">
    <SyntheticMessenger
      ref="syntheticMessengerRef"
      @error="onError"
      @connected="onConnected"
      @disconnected="onDisconnected"
      @onUnreadChange="onUnreadChange"
      @onOpenGallery="onOpenGallery"
    />

    <div
      class="messenger-container__support-trigger"
      v-show="!moderatorChatWasCalled"
    >
      <a-button
        type="primary"
        ghost
        class="messenger-container__support-trigger__button"
        @click="handleOpenModeratorChat"
      >
        {{ $t("messenger.contact_support") }}
      </a-button>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-ignore-next-line
import SyntheticMessenger from "@synthetic_ua/messenger-front/legacy"
import { mapActions, mapMutations, mapState } from "vuex"

import useDebounce from "@/composables/useDebounce"
import { LOGOUT, REFRESH_TOKEN } from "@/store/modules/auth/actionTypes.js"
import { getFullImagePath } from "@/utils/cdn"
import { getCookie } from "@/utils/cookies.js"

export default {
  components: { SyntheticMessenger },

  setup() {
    const { handleThrottle, resetThrottle } = useDebounce()

    return {
      handleThrottle,
      resetThrottle
    }
  },

  data() {
    return { openSupportChat: false, moderatorChatWasCalled: false, recconectTimeout: undefined }
  },

  inject: {
    showGallery: {
      default: () => {}
    }
  },

  computed: {
    ...mapState("common", {
      currentLocale: (state: any): string => state.currentLocale
    })
  },

  methods: {
    ...mapActions("auth", [LOGOUT, REFRESH_TOKEN]),
    ...mapMutations("messenger", ["SET_TOTAL_UNREAD_COUNT"]),

    async getMsgToken() {
      const access = getCookie("auth_access_mpo")
      const refresh = getCookie("auth_refresh_mpo")

      if (access) return access

      if (!refresh) {
        this[LOGOUT]()
        return null
      }

      try {
        const data = await this[REFRESH_TOKEN](refresh)

        if (!data || !data.access) {
          throw new Error("Unauthorized")
        }

        return data.access
      } catch {
        this[LOGOUT]()
        return null
      }
    },

    async connectMessenger() {
      this.resetThrottle("connect_msg")
      const sso_access_token = await this.getMsgToken()

      if (!sso_access_token) return

      try {
        await this.$refs.syntheticMessengerRef.install({
          sso_access_token,
          get_sso_token: this.getMsgToken,
          role: "seller",
          language: this.currentLocale,
          base_url: `${process.env.VUE_APP_MESSENGER_URL_XHR}/api/v2`,
          domain: `${process.env.VUE_APP_MESSENGER_DOMAIN}`,
          cdn_path_function: ({ filename, size = "full" }) => getFullImagePath(filename, size),
          marketplace_url: process.env.VUE_APP_MARKETPLACE_URL
        })
      } catch {
        this.handleThrottle("connect_msg", this.connectMessenger, 5000)
      }
    },

    async handleOpenChat({ uuid, role }) {
      try {
        const newChat = await this.$refs.syntheticMessengerRef.addNewChat({ uuid, role })

        if (role !== "moderator") return

        const moderUuid = `${process.env.VUE_APP_MESSENGER_MODERATOR_UUID}`.replace(/-/gm, "")

        if (newChat.owner.uuid === moderUuid) {
          this.moderatorChatWasCalled = true
          return
        }

        if (newChat.participants.some(({ uuid }) => uuid === moderUuid)) {
          this.moderatorChatWasCalled = true
        }

        this.openSupportChat = false
      } catch (error) {
        this.onError(error)
      }
    },

    handleOpenModeratorChat() {
      if (this.$refs.syntheticMessengerRef) {
        const uuid = process.env.VUE_APP_MESSENGER_MODERATOR_UUID
        this.handleOpenChat({ uuid, role: "moderator" })
      } else {
        setTimeout(this.handleOpenModeratorChat, 500)
      }
    },

    onUnreadChange(unread: number) {
      this.SET_TOTAL_UNREAD_COUNT(unread)
    },

    onOpenGallery({ media, index }) {
      this.showGallery({
        images: media.filter(({ type }) => type === "image").map(({ file }) => file),
        startIndex: index
      })
    },

    onConnected() {
      if (this.openSupportChat) return this.handleOpenModeratorChat()
    },

    onError(error: { type: string; code: number }) {
      if (error.code !== 401) return

      this.handleThrottle("connect_msg", this.connectMessenger, 5000)
    },

    onDisconnected() {
      this.handleThrottle("connect_msg", this.connectMessenger, 5000)
    }
  },

  mounted() {
    this.connectMessenger()

    if (this.$route.query?.openSupport) {
      this.openSupportChat = true
      this.$router.push({ query: {} })
    }
  },

  beforeDestroy() {
    this.$refs.syntheticMessengerRef.uninstall()
  }
}
</script>

<style lang="scss">
@import "@synthetic_ua/messenger-front/styles";
.messenger-container {
  height: calc(100vh - 150px);
  max-height: 1200px;
  position: relative;

  &__support-trigger {
    position: absolute;
    right: 16px;
    top: 0;
    z-index: 20;
    height: 80px;
    display: flex;
    align-items: center;

    &__button {
      border-radius: 12px !important;
      font-size: 14px !important;
      padding: 8px 16px !important;
      font-weight: 400 !important;
      height: fit-content !important;

      font-family: "Open Sans", sans-serif !important;
    }
  }
}
</style>
